import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { URL_GET_UNSEEN_COUNT } from '@/notifications/lib/notification-constants';
import { useAuthStore } from '@/auth/auth-store';

/**
 * Unread notification count for the Bell icon badge.
 *
 *   GET /api/Notifications/GetUserUnSeenNotificationCount
 *   response: **raw integer in body** (e.g. `42`) — NOT JSON-wrapped
 *
 * Mirrors Flutter `notification_center_data_source_impl.dart:51-60`
 * (`int.tryParse(utf8.decode(response.data))`).
 *
 * **api-client compatibility**: `parseResponse()` (api-client.ts:69-73)
 * calls `JSON.parse(text)` on a non-empty body. `JSON.parse("42")`
 * returns `42` (numeric literals are valid JSON), so `get<number>(URL)`
 * works directly without special-casing.
 *
 * **SignalR live updates**: `useNotificationCountRealtime` subscribes to
 * the Notifications hub and patches this query's data via
 * `qc.setQueryData(['notifications', 'unread-count'], event.count)`.
 * That hook is consumed alongside this one in the Bell icon container.
 *
 * Gated on `user` so the call doesn't fire pre-auth (would 401).
 */

export const UNREAD_NOTIFICATION_COUNT_KEY = [
  'notifications',
  'unread-count',
] as const;

export function useUnreadNotificationCount() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: UNREAD_NOTIFICATION_COUNT_KEY,
    queryFn: ({ signal }) => get<number>(URL_GET_UNSEEN_COUNT, { signal }),
    enabled: Boolean(user),
    // Refresh once per session-resume; SignalR keeps the count live in
    // between. We still set a stale window so a stale tab gets a clean
    // value when the user returns.
    staleTime: 60_000,
    // Don't fire a global toast — a flaky count is silently degraded
    // (badge stays at last value); user shouldn't see a "Something
    // went wrong" toast.
    meta: { skipGlobalError: true },
  });
}
